import type {
  FeaturePage,
  FeaturePageButtonStyle,
  FeaturePageViewType,
} from "../lib/feature-page";

export type {
  FeaturePage,
  FeaturePageAlignment,
  FeaturePageButton,
  FeaturePageButtonStyle,
  FeaturePageGrid,
  FeaturePageOrientation,
  FeaturePageSize,
  FeaturePageViewType,
} from "../lib/feature-page";

/**
 * JSON-compatible information pages shared by the website fallback renderer.
 *
 * The shape intentionally mirrors `FeaturePage` in the iOS app so an Unleash
 * variant can replace any fallback without a separate web-only content model.
 */

export type InformationPageLocale = "en" | "es";

export const informationPageKeys = [
  "about",
  "contact",
  "privacy",
  "terms",
  "cookies",
  "business",
] as const;

export type InformationPageKey = (typeof informationPageKeys)[number];
export type LocalizedFeaturePage = Record<InformationPageLocale, FeaturePage>;

const textNode = (
  id: string,
  viewType: Exclude<FeaturePageViewType, "button" | "grid" | "imageUrl" | "padding">,
  content: string,
  level?: number,
): FeaturePage => ({ id, viewType, content, ...(level === undefined ? {} : { level }) });

const buttonNode = (
  id: string,
  content: string,
  action: string,
  style: FeaturePageButtonStyle = "accent",
): FeaturePage => ({
  id,
  viewType: "button",
  content,
  button: { style, size: "regular", action },
});

const page = (id: string, columns: FeaturePage[]): FeaturePage => ({
  id,
  columns,
  alignment: "leading",
});

const ABOUT_EN = page("about-en", [
  textNode("about-en-kicker", "caption", "TOGETHER FOR EVERY PET"),
  textNode("about-en-title", "title", "About PetCollab"),
  textNode(
    "about-en-intro",
    "paragraph",
    "PetCollab is a place for the people who make a pet's world feel like home. Start with connection: meet familiar faces, share everyday moments, and create or join Circles centered on the pets, places, and interests you care about.",
  ),
  textNode("about-en-community-title", "subtitle", "Your community, at your pace"),
  textNode(
    "about-en-community-list",
    "bulletedParagraph",
    "Create and join Circles for your neighborhood, pet community, or shared interests.\nShare updates and discover what other pet people are enjoying.\nConnect through profiles, conversations, events, and adoption listings.",
  ),
  textNode("about-en-care-title", "subtitle", "A clearer care story"),
  textNode(
    "about-en-care-list",
    "bulletedParagraph",
    "Build a profile for each pet with identification and everyday details.\nKeep records, vaccination information, and medications organized.\nChoose what you share while keeping important care information close at hand.",
  ),
  textNode("about-en-discovery-title", "subtitle", "From connection to care"),
  textNode(
    "about-en-discovery-body",
    "paragraph",
    "Explore nearby pet-friendly places and service options, review available details, and manage bookings when a participating business offers them. PetCollab helps you move from a trusted recommendation to the next practical step.",
  ),
  textNode("about-en-business-title", "subtitle", "Built for both sides of pet care"),
  textNode(
    "about-en-business-body",
    "paragraph",
    "Pet professionals can present their locations and services, coordinate team and appointment details, and participate in the same community where pet owners are already connecting.",
  ),
  textNode(
    "about-en-note",
    "caption",
    "Feature availability may vary by location, account type, and release availability.",
  ),
]);

const ABOUT_ES = page("about-es", [
  textNode("about-es-kicker", "caption", "JUNTOS POR CADA MASCOTA"),
  textNode("about-es-title", "title", "Acerca de PetCollab"),
  textNode(
    "about-es-intro",
    "paragraph",
    "PetCollab es un lugar para las personas que hacen que el mundo de una mascota se sienta como en casa. Todo empieza con la conexión: conoce caras familiares, comparte momentos cotidianos y crea o únete a Círculos en torno a las mascotas, los lugares y los intereses que te importan.",
  ),
  textNode("about-es-community-title", "subtitle", "Tu comunidad, a tu ritmo"),
  textNode(
    "about-es-community-list",
    "bulletedParagraph",
    "Crea y únete a Círculos para tu vecindario, tu comunidad de mascotas o tus intereses.\nComparte novedades y descubre lo que disfrutan otras personas con mascotas.\nConecta mediante perfiles, conversaciones, eventos y publicaciones de adopción.",
  ),
  textNode("about-es-care-title", "subtitle", "Una historia de cuidado más clara"),
  textNode(
    "about-es-care-list",
    "bulletedParagraph",
    "Crea un perfil para cada mascota con su identificación y detalles cotidianos.\nMantén organizados sus registros, vacunas y medicamentos.\nElige qué compartir y conserva a la mano la información importante para su cuidado.",
  ),
  textNode("about-es-discovery-title", "subtitle", "De la conexión al cuidado"),
  textNode(
    "about-es-discovery-body",
    "paragraph",
    "Explora lugares que aceptan mascotas y opciones de servicios cercanos, consulta la información disponible y administra reservas cuando un negocio participante las ofrezca. PetCollab te ayuda a pasar de una recomendación confiable al siguiente paso práctico.",
  ),
  textNode("about-es-business-title", "subtitle", "Creado para ambos lados del cuidado"),
  textNode(
    "about-es-business-body",
    "paragraph",
    "Los profesionales de mascotas pueden presentar sus ubicaciones y servicios, coordinar la información de su equipo y sus citas, y participar en la misma comunidad donde las personas con mascotas ya están conectando.",
  ),
  textNode(
    "about-es-note",
    "caption",
    "La disponibilidad de las funciones puede variar según la ubicación, el tipo de cuenta y la versión.",
  ),
]);

const CONTACT_EN = page("contact-en", [
  textNode("contact-en-kicker", "caption", "WE'RE HERE TO HELP"),
  textNode("contact-en-title", "title", "Contact PetCollab"),
  textNode(
    "contact-en-intro",
    "paragraph",
    "Choose the route that best matches what you need. Including a few useful details helps the team understand your request without asking you to share sensitive information.",
  ),
  textNode("contact-en-support-title", "subtitle", "App support"),
  textNode(
    "contact-en-support-body",
    "paragraph",
    "For help with sign-in, profiles, Circles, community features, places, services, or bookings, start in the Help Center. If you report a technical issue, include your app version, device model, operating-system version, and the steps that led to the problem when possible.",
  ),
  buttonNode(
    "contact-en-support-button",
    "Open the Help Center",
    "https://petcollab.com/app/help-center",
  ),
  textNode("contact-en-data-title", "subtitle", "Privacy and data requests"),
  textNode(
    "contact-en-data-body",
    "paragraph",
    "To ask about access, correction, download, or deletion of account data, email records@petcollab.com from the address connected to your account when possible. Do not include a password, payment-card information, or sensitive pet records in your first message.",
  ),
  buttonNode(
    "contact-en-data-button",
    "Email the data team",
    "mailto:records@petcollab.com",
    "link",
  ),
  textNode("contact-en-urgent-title", "subtitle", "Urgent pet care"),
  textNode(
    "contact-en-urgent-body",
    "paragraph",
    "PetCollab is not an emergency or veterinary service. If a pet may be in danger or needs urgent care, contact a local veterinarian or emergency service directly.",
  ),
]);

const CONTACT_ES = page("contact-es", [
  textNode("contact-es-kicker", "caption", "ESTAMOS AQUÍ PARA AYUDAR"),
  textNode("contact-es-title", "title", "Contacta a PetCollab"),
  textNode(
    "contact-es-intro",
    "paragraph",
    "Elige la vía que mejor corresponda a lo que necesitas. Incluir algunos datos útiles ayuda al equipo a comprender tu solicitud sin pedirte que compartas información sensible.",
  ),
  textNode("contact-es-support-title", "subtitle", "Soporte de la app"),
  textNode(
    "contact-es-support-body",
    "paragraph",
    "Para obtener ayuda con el inicio de sesión, los perfiles, los Círculos, las funciones de comunidad, los lugares, los servicios o las reservas, comienza en el Centro de ayuda. Si informas un problema técnico, incluye cuando sea posible la versión de la app, el modelo de tu dispositivo, la versión del sistema operativo y los pasos que produjeron el problema.",
  ),
  buttonNode(
    "contact-es-support-button",
    "Abrir el Centro de ayuda",
    "https://petcollab.com/app/help-center",
  ),
  textNode("contact-es-data-title", "subtitle", "Privacidad y solicitudes de datos"),
  textNode(
    "contact-es-data-body",
    "paragraph",
    "Para solicitar acceso, corrección, descarga o eliminación de los datos de tu cuenta, escribe a records@petcollab.com desde el correo asociado a tu cuenta cuando sea posible. No incluyas contraseñas, datos de tarjetas ni registros sensibles de tu mascota en el primer mensaje.",
  ),
  buttonNode(
    "contact-es-data-button",
    "Escribir al equipo de datos",
    "mailto:records@petcollab.com",
    "link",
  ),
  textNode("contact-es-urgent-title", "subtitle", "Atención urgente para mascotas"),
  textNode(
    "contact-es-urgent-body",
    "paragraph",
    "PetCollab no es un servicio veterinario ni de emergencias. Si una mascota puede estar en peligro o necesita atención urgente, comunícate directamente con un veterinario o servicio de emergencias local.",
  ),
]);

const PRIVACY_EN = page("privacy-en", [
  textNode("privacy-en-label", "caption", "LAUNCH FALLBACK DRAFT"),
  textNode("privacy-en-title", "title", "Privacy Policy"),
  textNode(
    "privacy-en-intro",
    "paragraph",
    "This concise fallback describes how PetCollab is expected to handle information across the app and website at launch. The centrally managed production policy controls whenever it is available.",
  ),
  textNode("privacy-en-scope-title", "subtitle", "1. Information covered"),
  textNode(
    "privacy-en-scope-list",
    "bulletedParagraph",
    "Account and contact details, authentication information, and support communications.\nPet profiles, photos, identification details, care records, vaccination information, and medications you choose to add.\nConnections, Circle memberships, posts, messages, event activity, adoption activity, reviews, and other community contributions.\nLocation information when you enable it, plus device, diagnostic, usage, and security information.\nBusiness, location, service, team, availability, and booking information submitted by owners or participating businesses.",
  ),
  textNode("privacy-en-use-title", "subtitle", "2. How information is used"),
  textNode(
    "privacy-en-use-list",
    "bulletedParagraph",
    "Operate accounts, profiles, Circles, social features, discovery, records, and booking tools.\nShow content according to the audience and privacy choices connected to each feature.\nCommunicate about activity, support requests, service updates, and safety matters.\nProtect the platform, prevent misuse, diagnose problems, improve features, and meet legal obligations.",
  ),
  textNode("privacy-en-sharing-title", "subtitle", "3. When information is shared"),
  textNode(
    "privacy-en-sharing-body",
    "paragraph",
    "Information may be shown to people, Circles, or businesses you choose to interact with; to vendors that process information for PetCollab; or when reasonably necessary for legal compliance, safety, fraud prevention, or a business transition. Visibility depends on the feature and the settings available to you. Public contributions can be seen and reshared by others, so avoid posting information you do not want made public.",
  ),
  textNode("privacy-en-control-title", "subtitle", "4. Your choices and requests"),
  textNode(
    "privacy-en-control-body",
    "paragraph",
    "You can use account, pet, Circle, notification, device-permission, and content controls that are available in the product. You may request access, correction, download, or deletion by emailing records@petcollab.com. PetCollab may need to verify the request and may retain limited information when required for security, legal compliance, dispute handling, or legitimate recordkeeping.",
  ),
  textNode("privacy-en-retention-title", "subtitle", "5. Retention and protection"),
  textNode(
    "privacy-en-retention-body",
    "paragraph",
    "Information is retained for as long as reasonably needed to provide the service, honor user choices, resolve issues, and meet applicable obligations. PetCollab uses administrative, technical, and organizational safeguards intended to protect information, but no storage or transmission method can guarantee complete security.",
  ),
  textNode("privacy-en-children-title", "subtitle", "6. Children and regional rights"),
  textNode(
    "privacy-en-children-body",
    "paragraph",
    "PetCollab is designed for account holders who can lawfully agree to the applicable terms. A parent or guardian should manage any use involving a child. Privacy rights and age requirements can differ by location; the production policy and applicable law determine those rights.",
  ),
  textNode("privacy-en-changes-title", "subtitle", "7. Changes and contact"),
  textNode(
    "privacy-en-changes-body",
    "paragraph",
    "Material changes should be reflected in the centrally managed policy and communicated when required. Questions and privacy requests can be sent to records@petcollab.com.",
  ),
  textNode(
    "privacy-en-legal-note",
    "caption",
    "Product fallback only — not legal advice. PetCollab legal content is centrally managed and must be reviewed by qualified counsel before production use.",
  ),
]);

const PRIVACY_ES = page("privacy-es", [
  textNode("privacy-es-label", "caption", "BORRADOR ALTERNATIVO PARA EL LANZAMIENTO"),
  textNode("privacy-es-title", "title", "Política de privacidad"),
  textNode(
    "privacy-es-intro",
    "paragraph",
    "Este contenido alternativo y resumido describe cómo se espera que PetCollab maneje la información en la app y el sitio web durante el lanzamiento. La política de producción administrada centralmente prevalece en cuanto esté disponible.",
  ),
  textNode("privacy-es-scope-title", "subtitle", "1. Información incluida"),
  textNode(
    "privacy-es-scope-list",
    "bulletedParagraph",
    "Datos de cuenta y contacto, información de autenticación y comunicaciones con soporte.\nPerfiles, fotos, datos de identificación, registros de cuidado, vacunas y medicamentos que decidas agregar sobre tus mascotas.\nConexiones, membresías en Círculos, publicaciones, mensajes, actividad en eventos y adopciones, reseñas y otras contribuciones a la comunidad.\nInformación de ubicación cuando la habilites, además de datos del dispositivo, diagnóstico, uso y seguridad.\nInformación de negocios, ubicaciones, servicios, equipos, disponibilidad y reservas enviada por personas o negocios participantes.",
  ),
  textNode("privacy-es-use-title", "subtitle", "2. Cómo se utiliza la información"),
  textNode(
    "privacy-es-use-list",
    "bulletedParagraph",
    "Operar cuentas, perfiles, Círculos, funciones sociales, descubrimiento, registros y herramientas de reservas.\nMostrar contenido según el público y las opciones de privacidad vinculadas a cada función.\nComunicarnos sobre actividad, solicitudes de soporte, cambios del servicio y asuntos de seguridad.\nProteger la plataforma, prevenir usos indebidos, diagnosticar problemas, mejorar funciones y cumplir obligaciones legales.",
  ),
  textNode("privacy-es-sharing-title", "subtitle", "3. Cuándo se comparte la información"),
  textNode(
    "privacy-es-sharing-body",
    "paragraph",
    "La información puede mostrarse a personas, Círculos o negocios con los que decidas interactuar; a proveedores que procesen información para PetCollab; o cuando sea razonablemente necesario por cumplimiento legal, seguridad, prevención de fraude o una transición empresarial. La visibilidad depende de la función y de los controles disponibles. Otras personas pueden ver y volver a compartir las contribuciones públicas, así que evita publicar información que no quieras hacer pública.",
  ),
  textNode("privacy-es-control-title", "subtitle", "4. Tus opciones y solicitudes"),
  textNode(
    "privacy-es-control-body",
    "paragraph",
    "Puedes usar los controles disponibles para tu cuenta, mascotas, Círculos, notificaciones, permisos del dispositivo y contenido. Puedes solicitar acceso, corrección, descarga o eliminación escribiendo a records@petcollab.com. PetCollab puede necesitar verificar la solicitud y conservar información limitada cuando sea necesaria por seguridad, cumplimiento legal, resolución de disputas o mantenimiento legítimo de registros.",
  ),
  textNode("privacy-es-retention-title", "subtitle", "5. Conservación y protección"),
  textNode(
    "privacy-es-retention-body",
    "paragraph",
    "La información se conserva durante el tiempo razonablemente necesario para prestar el servicio, respetar las elecciones de las personas, resolver problemas y cumplir las obligaciones aplicables. PetCollab utiliza medidas administrativas, técnicas y organizativas destinadas a proteger la información, pero ningún método de almacenamiento o transmisión puede garantizar una seguridad completa.",
  ),
  textNode("privacy-es-children-title", "subtitle", "6. Menores y derechos regionales"),
  textNode(
    "privacy-es-children-body",
    "paragraph",
    "PetCollab está diseñado para titulares de cuentas que pueden aceptar legalmente los términos aplicables. Un padre, madre o tutor debe administrar cualquier uso que involucre a un menor. Los derechos de privacidad y los requisitos de edad pueden variar según la ubicación; la política de producción y la ley aplicable determinan esos derechos.",
  ),
  textNode("privacy-es-changes-title", "subtitle", "7. Cambios y contacto"),
  textNode(
    "privacy-es-changes-body",
    "paragraph",
    "Los cambios importantes deben reflejarse en la política administrada centralmente y comunicarse cuando corresponda. Las preguntas y solicitudes de privacidad pueden enviarse a records@petcollab.com.",
  ),
  textNode(
    "privacy-es-legal-note",
    "caption",
    "Contenido alternativo del producto — no constituye asesoría legal. El contenido legal de PetCollab se administra centralmente y debe ser revisado por profesionales jurídicos calificados antes de usarse en producción.",
  ),
]);

const TERMS_EN = page("terms-en", [
  textNode("terms-en-label", "caption", "LAUNCH FALLBACK DRAFT"),
  textNode("terms-en-title", "title", "Terms of Use"),
  textNode(
    "terms-en-intro",
    "paragraph",
    "These concise fallback terms describe the expected rules for using PetCollab at launch. By creating an account or using the service, you agree to the centrally managed production terms when they are available.",
  ),
  textNode("terms-en-account-title", "subtitle", "1. Accounts and eligibility"),
  textNode(
    "terms-en-account-body",
    "paragraph",
    "Provide accurate account information, keep sign-in credentials secure, and use PetCollab only if you can lawfully agree to these terms. You are responsible for activity performed through your account and should promptly report suspected unauthorized access.",
  ),
  textNode("terms-en-community-title", "subtitle", "2. Community participation"),
  textNode(
    "terms-en-community-body",
    "paragraph",
    "Use profiles, Circles, posts, messages, reviews, events, and adoption features respectfully. Do not harass others, impersonate a person or business, misrepresent a pet or service, violate privacy, post unlawful or harmful material, or interfere with the platform. Only share content you have the right to use.",
  ),
  textNode("terms-en-care-title", "subtitle", "3. Pet information and care"),
  textNode(
    "terms-en-care-body",
    "paragraph",
    "Pet profiles, records, vaccination details, medication tools, community content, and provider listings are organizational and informational resources. They do not replace professional veterinary advice, diagnosis, treatment, or emergency care. Confirm important information with the appropriate professional before relying on it.",
  ),
  textNode("terms-en-booking-title", "subtitle", "4. Places, services, and bookings"),
  textNode(
    "terms-en-booking-body",
    "paragraph",
    "PetCollab can help people discover places and services and coordinate bookings with participating businesses. Businesses are responsible for their descriptions, availability, qualifications, pricing, cancellation terms, and delivery of services. Pet owners are responsible for deciding whether a place or provider fits their needs and for reviewing the terms presented for a booking.",
  ),
  textNode("terms-en-business-title", "subtitle", "5. Business accounts"),
  textNode(
    "terms-en-business-body",
    "paragraph",
    "A person managing a business profile must be authorized to do so and must keep organization, location, team, service, schedule, event, adoption, and appointment information accurate. Businesses remain responsible for required licenses, insurance, permissions, taxes, consumer obligations, and compliance with applicable law.",
  ),
  textNode("terms-en-content-title", "subtitle", "6. Content and platform rights"),
  textNode(
    "terms-en-content-body",
    "paragraph",
    "You retain ownership of content you submit. You give PetCollab the permissions reasonably needed to host, process, display, and distribute that content to operate the service in line with your feature and visibility choices. PetCollab and its licensors retain rights in the app, website, branding, software, and original platform content.",
  ),
  textNode("terms-en-enforcement-title", "subtitle", "7. Availability and enforcement"),
  textNode(
    "terms-en-enforcement-body",
    "paragraph",
    "Features may change, pause, or vary by location and account type. PetCollab may review content and restrict content or account access when reasonably necessary to enforce these terms, protect users or pets, investigate misuse, or comply with law. The production terms should state the applicable warranties, liability limits, dispute process, and governing law.",
  ),
  textNode("terms-en-contact-title", "subtitle", "8. Questions and updates"),
  textNode(
    "terms-en-contact-body",
    "paragraph",
    "Updated terms should be posted through the centrally managed content source and communicated when required. For product questions, visit https://petcollab.com/app/help-center.",
  ),
  textNode(
    "terms-en-legal-note",
    "caption",
    "Product fallback only — not legal advice. PetCollab legal content is centrally managed and must be reviewed by qualified counsel before production use.",
  ),
]);

const TERMS_ES = page("terms-es", [
  textNode("terms-es-label", "caption", "BORRADOR ALTERNATIVO PARA EL LANZAMIENTO"),
  textNode("terms-es-title", "title", "Términos de uso"),
  textNode(
    "terms-es-intro",
    "paragraph",
    "Estos términos alternativos y resumidos describen las reglas previstas para usar PetCollab durante el lanzamiento. Al crear una cuenta o usar el servicio, aceptas los términos de producción administrados centralmente en cuanto estén disponibles.",
  ),
  textNode("terms-es-account-title", "subtitle", "1. Cuentas y elegibilidad"),
  textNode(
    "terms-es-account-body",
    "paragraph",
    "Proporciona información de cuenta correcta, protege tus credenciales y usa PetCollab solo si puedes aceptar legalmente estos términos. Eres responsable de la actividad realizada desde tu cuenta y debes informar de inmediato cualquier acceso no autorizado que sospeches.",
  ),
  textNode("terms-es-community-title", "subtitle", "2. Participación en la comunidad"),
  textNode(
    "terms-es-community-body",
    "paragraph",
    "Usa los perfiles, Círculos, publicaciones, mensajes, reseñas, eventos y funciones de adopción con respeto. No acoses a otras personas, suplantes la identidad de una persona o negocio, tergiverses una mascota o un servicio, vulneres la privacidad, publiques material ilegal o dañino ni interfieras con la plataforma. Comparte únicamente contenido que tengas derecho a usar.",
  ),
  textNode("terms-es-care-title", "subtitle", "3. Información y cuidado de mascotas"),
  textNode(
    "terms-es-care-body",
    "paragraph",
    "Los perfiles, registros, datos de vacunas, herramientas de medicamentos, contenido de la comunidad y fichas de proveedores son recursos organizativos e informativos. No sustituyen el consejo, diagnóstico, tratamiento ni cuidado de emergencia de un profesional veterinario. Confirma la información importante con el profesional adecuado antes de utilizarla para tomar decisiones.",
  ),
  textNode("terms-es-booking-title", "subtitle", "4. Lugares, servicios y reservas"),
  textNode(
    "terms-es-booking-body",
    "paragraph",
    "PetCollab puede ayudar a descubrir lugares y servicios y a coordinar reservas con negocios participantes. Los negocios son responsables de sus descripciones, disponibilidad, cualificaciones, precios, condiciones de cancelación y prestación de servicios. Las personas con mascotas son responsables de decidir si un lugar o proveedor responde a sus necesidades y de revisar las condiciones presentadas para cada reserva.",
  ),
  textNode("terms-es-business-title", "subtitle", "5. Cuentas de negocios"),
  textNode(
    "terms-es-business-body",
    "paragraph",
    "Quien administre el perfil de un negocio debe estar autorizado y mantener correcta la información de la organización, ubicaciones, equipo, servicios, horarios, eventos, adopciones y citas. Los negocios siguen siendo responsables de las licencias, seguros, permisos, impuestos, obligaciones con consumidores y cumplimiento de la ley que correspondan.",
  ),
  textNode("terms-es-content-title", "subtitle", "6. Contenido y derechos de la plataforma"),
  textNode(
    "terms-es-content-body",
    "paragraph",
    "Conservas la propiedad del contenido que envías. Concedes a PetCollab los permisos razonablemente necesarios para alojar, procesar, mostrar y distribuir ese contenido con el fin de operar el servicio de acuerdo con las funciones y opciones de visibilidad que elijas. PetCollab y sus licenciantes conservan los derechos sobre la app, el sitio web, la marca, el software y el contenido original de la plataforma.",
  ),
  textNode("terms-es-enforcement-title", "subtitle", "7. Disponibilidad y cumplimiento"),
  textNode(
    "terms-es-enforcement-body",
    "paragraph",
    "Las funciones pueden cambiar, pausarse o variar según la ubicación y el tipo de cuenta. PetCollab puede revisar contenido y restringir contenido o acceso a cuentas cuando sea razonablemente necesario para aplicar estos términos, proteger a personas o mascotas, investigar usos indebidos o cumplir la ley. Los términos de producción deben establecer las garantías aplicables, los límites de responsabilidad, el proceso de resolución de disputas y la legislación correspondiente.",
  ),
  textNode("terms-es-contact-title", "subtitle", "8. Preguntas y actualizaciones"),
  textNode(
    "terms-es-contact-body",
    "paragraph",
    "Los términos actualizados deben publicarse mediante la fuente de contenido administrada centralmente y comunicarse cuando corresponda. Para preguntas sobre el producto, visita https://petcollab.com/app/help-center.",
  ),
  textNode(
    "terms-es-legal-note",
    "caption",
    "Contenido alternativo del producto — no constituye asesoría legal. El contenido legal de PetCollab se administra centralmente y debe ser revisado por profesionales jurídicos calificados antes de usarse en producción.",
  ),
]);

const COOKIES_EN = page("cookies-en", [
  textNode("cookies-en-label", "caption", "LAUNCH FALLBACK DRAFT"),
  textNode("cookies-en-title", "title", "Cookie Policy"),
  textNode(
    "cookies-en-intro",
    "paragraph",
    "At launch, this fallback assumes the PetCollab website uses only essential browser storage for language, color theme, and basic site operation. The policy and consent controls must be updated before optional analytics, embedded media, or advertising technologies are enabled.",
  ),
  textNode("cookies-en-essential-title", "subtitle", "Essential storage"),
  textNode(
    "cookies-en-essential-body",
    "paragraph",
    "Cookies or similar browser storage may remember your language and light-or-dark appearance choice, preserve navigation state, support security, and keep the website working as expected. These items are not used by this fallback for cross-site advertising.",
  ),
  textNode("cookies-en-optional-title", "subtitle", "Optional technologies"),
  textNode(
    "cookies-en-optional-body",
    "paragraph",
    "If PetCollab later adds analytics or third-party media, the site should describe each category, provider, purpose, and duration and request consent where required before activation. Following a link to another website can expose you to that site's separate cookie and privacy practices.",
  ),
  textNode("cookies-en-app-title", "subtitle", "The mobile app"),
  textNode(
    "cookies-en-app-body",
    "paragraph",
    "Mobile apps generally use device storage, permissions, identifiers, and software development kits rather than browser cookies. Those practices belong in the Privacy Policy and in applicable device or in-app controls.",
  ),
  textNode("cookies-en-controls-title", "subtitle", "Your controls"),
  textNode(
    "cookies-en-controls-body",
    "paragraph",
    "You can clear or block browser storage in your browser settings, though blocking essential storage may prevent preferences or parts of the site from working correctly. PetCollab should also provide consent controls before any nonessential category is introduced where required.",
  ),
  textNode(
    "cookies-en-legal-note",
    "caption",
    "Product fallback only — not legal advice. PetCollab legal content is centrally managed and must be reviewed by qualified counsel before production use.",
  ),
]);

const COOKIES_ES = page("cookies-es", [
  textNode("cookies-es-label", "caption", "BORRADOR ALTERNATIVO PARA EL LANZAMIENTO"),
  textNode("cookies-es-title", "title", "Política de cookies"),
  textNode(
    "cookies-es-intro",
    "paragraph",
    "Para el lanzamiento, este contenido alternativo supone que el sitio web de PetCollab utiliza únicamente almacenamiento esencial del navegador para el idioma, el tema de color y el funcionamiento básico. La política y los controles de consentimiento deben actualizarse antes de habilitar analítica opcional, contenido multimedia integrado o tecnologías publicitarias.",
  ),
  textNode("cookies-es-essential-title", "subtitle", "Almacenamiento esencial"),
  textNode(
    "cookies-es-essential-body",
    "paragraph",
    "Las cookies o tecnologías similares del navegador pueden recordar tu idioma y tu preferencia de apariencia clara u oscura, conservar el estado de navegación, apoyar la seguridad y permitir que el sitio funcione según lo previsto. Este contenido alternativo no utiliza esos elementos para publicidad entre sitios.",
  ),
  textNode("cookies-es-optional-title", "subtitle", "Tecnologías opcionales"),
  textNode(
    "cookies-es-optional-body",
    "paragraph",
    "Si PetCollab incorpora posteriormente analítica o contenido de terceros, el sitio deberá describir cada categoría, proveedor, propósito y duración, y solicitar consentimiento antes de activarlos cuando corresponda. Al seguir un enlace a otro sitio web, se aplican las prácticas de cookies y privacidad propias de ese sitio.",
  ),
  textNode("cookies-es-app-title", "subtitle", "La app móvil"),
  textNode(
    "cookies-es-app-body",
    "paragraph",
    "Las apps móviles suelen utilizar almacenamiento del dispositivo, permisos, identificadores y kits de desarrollo de software en lugar de cookies del navegador. Esas prácticas deben explicarse en la Política de privacidad y en los controles correspondientes del dispositivo o de la app.",
  ),
  textNode("cookies-es-controls-title", "subtitle", "Tus controles"),
  textNode(
    "cookies-es-controls-body",
    "paragraph",
    "Puedes borrar o bloquear el almacenamiento desde la configuración de tu navegador, aunque bloquear el almacenamiento esencial puede impedir que las preferencias o algunas partes del sitio funcionen correctamente. PetCollab también debe ofrecer controles de consentimiento antes de introducir categorías no esenciales cuando sea obligatorio.",
  ),
  textNode(
    "cookies-es-legal-note",
    "caption",
    "Contenido alternativo del producto — no constituye asesoría legal. El contenido legal de PetCollab se administra centralmente y debe ser revisado por profesionales jurídicos calificados antes de usarse en producción.",
  ),
]);

const BUSINESS_EN = page("business-en", [
  textNode("business-en-kicker", "caption", "FOR PET PROFESSIONALS"),
  textNode("business-en-title", "title", "Bring your pet business into the community"),
  textNode(
    "business-en-intro",
    "paragraph",
    "PetCollab is connection-first. Build a useful presence where pet owners discover one another, exchange recommendations, and look for the next place or professional who can help their pets thrive.",
  ),
  textNode("business-en-tools-title", "subtitle", "Tools for the work behind great care"),
  textNode(
    "business-en-tools-list",
    "bulletedParagraph",
    "Present your organization, locations, hours, contact details, and service information.\nCoordinate team members, availability, services, and appointments.\nPublish relevant events or adoption listings when those tools fit your organization.\nKeep details current so pet owners can make informed decisions and reach the right location.",
  ),
  textNode("business-en-start-title", "subtitle", "Start with the essentials"),
  textNode(
    "business-en-start-list",
    "bulletedParagraph",
    "Create or update your business profile.\nAdd accurate locations and service details.\nInvite authorized team members and keep schedules current.\nParticipate in the community with useful, respectful information.",
  ),
  buttonNode(
    "business-en-cta",
    "Add or manage my business",
    "https://petcollab.com/app/add-edit-service-company",
  ),
  textNode(
    "business-en-note",
    "caption",
    "Business tools may vary by organization type, location, and release availability. Creating a profile does not guarantee visibility or bookings.",
  ),
]);

const BUSINESS_ES = page("business-es", [
  textNode("business-es-kicker", "caption", "PARA PROFESIONALES DE MASCOTAS"),
  textNode("business-es-title", "title", "Haz que tu negocio de mascotas forme parte de la comunidad"),
  textNode(
    "business-es-intro",
    "paragraph",
    "En PetCollab, la conexión es lo primero. Crea una presencia útil donde las personas con mascotas se conocen, intercambian recomendaciones y buscan el siguiente lugar o profesional que pueda ayudar a sus mascotas a prosperar.",
  ),
  textNode("business-es-tools-title", "subtitle", "Herramientas para el trabajo detrás de un gran cuidado"),
  textNode(
    "business-es-tools-list",
    "bulletedParagraph",
    "Presenta tu organización, ubicaciones, horarios, datos de contacto e información de servicios.\nCoordina integrantes del equipo, disponibilidad, servicios y citas.\nPublica eventos relevantes o anuncios de adopción cuando esas herramientas correspondan a tu organización.\nMantén los datos actualizados para que las personas puedan tomar decisiones informadas y comunicarse con la ubicación correcta.",
  ),
  textNode("business-es-start-title", "subtitle", "Comienza con lo esencial"),
  textNode(
    "business-es-start-list",
    "bulletedParagraph",
    "Crea o actualiza el perfil de tu negocio.\nAgrega ubicaciones y servicios con información correcta.\nInvita a integrantes autorizados de tu equipo y mantén los horarios al día.\nParticipa en la comunidad con información útil y respetuosa.",
  ),
  buttonNode(
    "business-es-cta",
    "Agregar o administrar mi negocio",
    "https://petcollab.com/app/add-edit-service-company",
  ),
  textNode(
    "business-es-note",
    "caption",
    "Las herramientas para negocios pueden variar según el tipo de organización, la ubicación y la versión. Crear un perfil no garantiza visibilidad ni reservas.",
  ),
]);

export const about: LocalizedFeaturePage = { en: ABOUT_EN, es: ABOUT_ES };
export const contact: LocalizedFeaturePage = { en: CONTACT_EN, es: CONTACT_ES };
export const privacy: LocalizedFeaturePage = { en: PRIVACY_EN, es: PRIVACY_ES };
export const terms: LocalizedFeaturePage = { en: TERMS_EN, es: TERMS_ES };
export const cookies: LocalizedFeaturePage = { en: COOKIES_EN, es: COOKIES_ES };
export const business: LocalizedFeaturePage = { en: BUSINESS_EN, es: BUSINESS_ES };

export const fallbackInformationPages: Record<InformationPageKey, LocalizedFeaturePage> = {
  about,
  contact,
  privacy,
  terms,
  cookies,
  business,
};

/**
 * Existing English flags are kept as-is. Contact and cookie policy are proposed
 * additions. Spanish variants use the agreed `.es` suffix.
 */
export const informationPageFlags = {
  about: { en: "information.about", es: "information.about.es" },
  contact: { en: "information.contact-us", es: "information.contact-us.es" },
  privacy: { en: "information.privacy-policy", es: "information.privacy-policy.es" },
  terms: { en: "information.terms-of-use", es: "information.terms-of-use.es" },
  cookies: { en: "information.cookie-policy", es: "information.cookie-policy.es" },
  business: {
    en: "information.registering-your-business",
    es: "information.registering-your-business.es",
  },
} as const satisfies Record<
  InformationPageKey,
  Record<InformationPageLocale, string>
>;
